import React from 'react';
import { Crown, Trophy, Star } from 'lucide-react';
import { useWaitlistCount } from '../hooks/useWaitlistCount';
import { trackTierSelection } from '../lib/analytics';
import { FadeIn } from './animations/FadeIn';

export function WaitlistTiers() {
  const { count } = useWaitlistCount();

  const tiers = [
    {
      name: "Grundare",
      icon: Crown,
      signupsBefore: 500,
      benefits: [
        "3 månader gratis användning",
        "Livstids VIP-support",
        "Tidig åtkomst till nya funktioner"
      ],
      available: count < 500,
      current: count < 500,
      level: 1
    },
    {
      name: "Pionjär",
      icon: Trophy,
      signupsBefore: 1000,
      opensAt: 500,
      benefits: [
        "2 månader gratis användning",
        "Prioriterad support",
        "Tidig åtkomst till nya funktioner"
      ],
      available: count < 1000,
      current: count >= 500 && count < 1000,
      level: 2
    },
    {
      name: "Tidig användare",
      icon: Star,
      signupsBefore: 2000,
      opensAt: 1000,
      benefits: [
        "1 månad gratis användning",
        "Tidig åtkomst till nya funktioner"
      ],
      available: count < 2000,
      current: count >= 1000 && count < 2000,
      level: 3
    }
  ];

  const handleTierClick = (tier: typeof tiers[0]) => {
    trackTierSelection(
      tier.name,
      tier.level,
      tier.signupsBefore - count
    );
  };

  return (
    <div className="grid md:grid-cols-3 gap-6 md:gap-12">
      {tiers.map((tier, index) => (
        <FadeIn 
          key={index}
          delay={index * 0.1}
          direction={index === 0 ? 'right' : index === 2 ? 'left' : 'up'}
        >
          <div 
            onClick={() => handleTierClick(tier)}
            className={`relative bg-white p-6 md:p-8 rounded-2xl transition-all duration-300 border cursor-pointer hover:shadow-lg ${
              tier.current 
                ? 'border-blue-500 ring-2 ring-blue-500 ring-opacity-50 transform scale-105 z-10' 
                : 'border-gray-100 opacity-75 hover:opacity-100'
            }`}
          >
            {tier.current && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                Rekryterar nu
              </div>
            )}
            <div className="flex items-center gap-3 mb-6">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                tier.current ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-400'
              }`}>
                <tier.icon size={24} />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{tier.name}</h3>
                <p className="text-sm text-gray-500">
                  {tier.current 
                    ? `${tier.signupsBefore - count} platser kvar` 
                    : count >= tier.signupsBefore 
                      ? 'Fullbokad'
                      : `Öppnar vid ${tier.opensAt || tier.signupsBefore} anmälningar`}
                </p>
              </div>
            </div>
            <ul className="space-y-4">
              {tier.benefits.map((benefit, benefitIndex) => (
                <li key={benefitIndex} className="flex items-center gap-3">
                  <div className={`flex-shrink-0 w-1.5 h-1.5 rounded-full ${
                    tier.current ? "bg-blue-500" : "bg-gray-300"
                  }`} />
                  <span className={`${tier.current ? "text-gray-600" : "text-gray-400"} leading-tight`}>
                    {benefit}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </FadeIn>
      ))}
    </div>
  );
}